import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { API_BASE_URL, FileParameter, IAttachmentDto } from '@fdm/api-client';
import { tap } from 'rxjs';

export interface CreateNotePayload {
  title: string;
  text: string;
  files?: FileParameter[];
  
}

export interface UpdateNotePayload extends CreateNotePayload {
  noteId: number;
  filesAttachedNoteId: number;
  deleteAttachmentIds?: number[];
}

@Injectable()
export class NoteAttachmentApiService {
  private _http = inject(HttpClient);

  private _baseUrl = inject(API_BASE_URL);

  private _url = this._baseUrl + '/api/NotesAndAttachments';

  private _riskCommentId: number | null = null;

  setRiskCommentId(id: number) {
    this._riskCommentId = id;
  }

  public createNote(generalTabId: string, payload: CreateNotePayload) {
    const formData = new FormData();

    formData.append("title", payload.title);

    if (!payload.text) {
      throw new globalThis.Error("The parameter 'text' cannot be null.");
    } else {
      formData.append("text", payload.text);
    }

    if (payload.files?.length) {
      payload.files.forEach((file) => {
        formData.append(`files`, file.data, file.fileName ? file.fileName : "file");
      });
    }

    return this._http.post(`${this._url}/${generalTabId}/notes`, formData, { 
      headers: {
        'Content-Type': 'multipart/form-data'
      } 
    });
  }

  public updateNote(generalTabId: string, payload: UpdateNotePayload) {
    const formData = new FormData();
    
    if (!payload.noteId) {
      throw new globalThis.Error("The parameter 'noteId' cannot be null.");
    } else {
      formData.append("noteId", payload.noteId.toString());
    }

    if (!payload.title) {
      throw new globalThis.Error("The parameter 'title' cannot be null.");
    } else {
      formData.append("title", payload.title);
    }
    
    if (!payload.text) {
      throw new globalThis.Error("The parameter 'text' cannot be null.");
    } else {
      formData.append("text", payload.text);
    }

    if (!payload.filesAttachedNoteId) {
      throw new globalThis.Error("The parameter 'filesAttachedNoteId' cannot be null.");
    } else {
      formData.append("filesAttachedNoteId", payload.filesAttachedNoteId.toString());
    }

    if (payload.deleteAttachmentIds?.length) {
      payload.deleteAttachmentIds.forEach((id) => {
        formData.append("deleteAttachmentIds", id.toString());
      });
    }

    if (payload.files?.length) {
      payload.files.forEach((file) => {
        formData.append(`addFiles`, file.data, file.fileName ? file.fileName : "addFiles");
      });
    }

    return this._http.put(`${this._url}/notes/${generalTabId}?approverUserRole=Initiator`, formData, { 
      headers: {
        'Content-Type': 'multipart/form-data'
      } 
    });
  }

  public getAttachments(generalTabId: string) {
    return this._http.get<IAttachmentDto[]>(`${this._url}/${generalTabId}/attachments`);
  }

  public preview(blobName: string) {
    return this._http
      .get(`${this._url}/attachments/${blobName}/preview`, {
        responseType: 'blob',
      })
      .pipe(
        tap((blob) => {
        this._processPreview(blob)
        })
      );
  }

  public delete(generalTabId: string) {
    return this._http.delete(`${this._url}/notes/${generalTabId}?approverUserRole=Initiator`);
  }

  public download(blobName: string) {
    return this._riskCommentId
      ? this._downloadRiskEvaluationFile(blobName)
      : this._downloadNoteFile(blobName);
  }

  private _downloadRiskEvaluationFile(blobName: string) {
    return this._http
      .get(`${this._url}/riskevaluation/${blobName}/download`, {
        responseType: 'blob',
      })
      .pipe(tap((blob) => this._processDownloading(blobName, blob)));
  }

  private _downloadNoteFile(blobName: string) {
    return this._http
      .get(`${this._url}/attachments/${blobName}/download`, {
        responseType: 'blob',
      })
      .pipe(tap((blob) => this._processDownloading(blobName, blob)));
  }

  private _processDownloading(fileName: string, blob: Blob) {
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(objectUrl);
  }

  private _processPreview(blob: Blob) {
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.target = '_blank';
    a.click();
  }
}
